/**
 * Puanlama.
 *
 * Buradaki her fonksiyon SAF: girdi deneme listesi, çıktı sayı. Hiçbir
 * durum tutmaz. Sebebi şu: norm çalışması yayımlandığında veritabanındaki
 * ham denemeleri yeniden puanlamak gerekecek ve o işi aynı kodla yapmak
 * istiyoruz. Eşik hesabı bile merdivenin canlı durumuna değil, kaydedilmiş
 * seviye dizisine bakar.
 */

import type { ScoreRow, TrialRecord } from './types'

export const SCORING_VERSION = 'v1'

export function mean(xs: number[]): number {
  if (xs.length === 0) return 0
  let s = 0
  for (const x of xs) s += x
  return s / xs.length
}

/** Örneklem standart sapması (n-1) */
export function sd(xs: number[]): number {
  if (xs.length < 2) return 0
  const m = mean(xs)
  let s = 0
  for (const x of xs) s += (x - m) ** 2
  return Math.sqrt(s / (xs.length - 1))
}

/**
 * Normal dağılımın ters birikimli fonksiyonu (Acklam yaklaşımı).
 * d' hesabı için gerekli; harici bağımlılık getirmeye değmez.
 */
export function probit(p: number): number {
  if (p <= 0 || p >= 1) throw new Error(`probit: p aralık dışı (${p})`)
  const a = [-3.969683028665376e1, 2.209460984245205e2, -2.759285104469687e2,
             1.383577518672690e2, -3.066479806614716e1, 2.506628277459239]
  const b = [-5.447609879822406e1, 1.615858368580409e2, -1.556989798598866e2,
             6.680131188771972e1, -1.328068155288572e1]
  const c = [-7.784894002430293e-3, -3.223964580411365e-1, -2.400758277161838,
             -2.549732539343734, 4.374664141464968, 2.938163982698783]
  const d = [7.784695709041462e-3, 3.224671290700398e-1, 2.445134137142996,
             3.754408661907416]
  const pl = 0.02425

  if (p < pl) {
    const q = Math.sqrt(-2 * Math.log(p))
    return (((((c[0]! * q + c[1]!) * q + c[2]!) * q + c[3]!) * q + c[4]!) * q + c[5]!) /
      ((((d[0]! * q + d[1]!) * q + d[2]!) * q + d[3]!) * q + 1)
  }
  if (p > 1 - pl) {
    const q = Math.sqrt(-2 * Math.log(1 - p))
    return -(((((c[0]! * q + c[1]!) * q + c[2]!) * q + c[3]!) * q + c[4]!) * q + c[5]!) /
      ((((d[0]! * q + d[1]!) * q + d[2]!) * q + d[3]!) * q + 1)
  }
  const q = p - 0.5
  const r = q * q
  return (((((a[0]! * r + a[1]!) * r + a[2]!) * r + a[3]!) * r + a[4]!) * r + a[5]!) * q /
    (((((b[0]! * r + b[1]!) * r + b[2]!) * r + b[3]!) * r + b[4]!) * r + 1)
}

/**
 * d' — sinyal ayırt etme gücü.
 *
 * Loglineer düzeltme kullanılır (Hautus 1995): her hücreye 0,5 eklenir.
 * Düzeltme olmadan kusursuz veya sıfır performansta oran 0/1 çıkar ve
 * probit sonsuza gider. Çocuk ölçümlerinde kusursuz blok sık görülür.
 */
export function dPrime(hits: number, misses: number, falseAlarms: number, correctRejections: number): number {
  const targets = hits + misses
  const lures = falseAlarms + correctRejections
  if (targets === 0 || lures === 0) return 0
  const h = (hits + 0.5) / (targets + 1)
  const f = (falseAlarms + 0.5) / (lures + 1)
  return probit(h) - probit(f)
}

/** Yalnızca geçerli denemeler analize girer */
export function validTrials(trials: TrialRecord[]): TrialRecord[] {
  return trials.filter((t) => t.valid)
}

/**
 * Eşik: kaydedilmiş seviye dizisinden dönüş noktalarını bulur.
 *
 * Merdivenin canlı durumuna bakmaz; veritabanından okunan `difficulty_level`
 * dizisi yeter. Yeniden puanlamanın mümkün olmasının sebebi bu.
 */
export function thresholdFromLevels(
  trials: TrialRecord[],
  opts: { dropFirst?: number; useLast?: number } = {},
): { value: number; reversalsUsed: number; fallback: boolean } {
  const dropFirst = opts.dropFirst ?? 2
  const useLast = opts.useLast ?? 6

  const levels = validTrials(trials).map((t) => t.level)
  const reversals: number[] = []
  let dir = 0

  for (let i = 1; i < levels.length; i++) {
    const delta = levels[i]! - levels[i - 1]!
    if (delta === 0) continue
    const d = delta > 0 ? 1 : -1
    if (dir !== 0 && d !== dir) reversals.push(levels[i - 1]!)
    dir = d
  }

  const usable = reversals.slice(dropFirst)
  if (usable.length >= useLast) {
    return { value: mean(usable.slice(-useLast)), reversalsUsed: useLast, fallback: false }
  }
  if (usable.length >= 2) {
    const even = usable.length % 2 === 0 ? usable : usable.slice(0, -1)
    return { value: mean(even), reversalsUsed: even.length, fallback: true }
  }
  return {
    value: levels.length > 0 ? mean(levels.slice(-Math.min(8, levels.length))) : 0,
    reversalsUsed: 0,
    fallback: true,
  }
}

/** Hız görevleri için ortak skorlar */
export function rtScores(trials: TrialRecord[], domain: TrialRecord['domain']): ScoreRow[] {
  const valid = validTrials(trials)
  // Yalnızca DOĞRU tepkilerin süresi ortalanır: yanlış cevabın süresi
  // hızı değil tahmini ölçer
  const rts = valid.filter((t) => t.correct && t.rtMs !== null).map((t) => t.rtMs!)
  if (rts.length === 0) return []

  return [
    { domain, metric: 'rt_mean', value: round(mean(rts), 1), unit: 'ms', trialsUsed: rts.length },
    // rt_sd dikkat ölçümünde ortalamadan daha bilgilendirici: değişkenlik
    // yüksekse çocuk bazen çok hızlı bazen çok yavaş, yani dikkati dalgalı
    { domain, metric: 'rt_sd', value: round(sd(rts), 1), unit: 'ms', trialsUsed: rts.length },
  ]
}

export function accuracyScore(trials: TrialRecord[], domain: TrialRecord['domain']): ScoreRow[] {
  const valid = validTrials(trials)
  if (valid.length === 0) return []
  const correct = valid.filter((t) => t.correct).length
  return [
    {
      domain,
      metric: 'accuracy',
      value: round(correct / valid.length, 4),
      unit: 'ratio',
      trialsUsed: valid.length,
    },
  ]
}

/**
 * Sinyal ayırt etme skorları.
 *
 * `commissionDomain` ayrı veriliyor: CPT'de hedef olmayana basmak bir
 * DÜRTÜ KONTROLÜ göstergesidir, ama n-back'te aynı hata bellek
 * hatasıdır — yanlış harfi hedef sanmıştır. İkisini tek alanda
 * toplamak raporda "dürtü kontrolü" başlığı altında iki ayrı ve
 * çelişen sayı üretiyordu.
 */
export function signalScores(
  trials: TrialRecord[],
  domain: TrialRecord['domain'],
  commissionDomain: TrialRecord['domain'] = 'inhibition',
): ScoreRow[] {
  const valid = validTrials(trials)
  if (valid.length === 0) return []
  const hits = valid.filter((t) => t.outcome === 'hit').length
  const misses = valid.filter((t) => t.outcome === 'miss').length
  const fa = valid.filter((t) => t.outcome === 'false_alarm').length
  const cr = valid.filter((t) => t.outcome === 'correct_rejection').length

  const rows: ScoreRow[] = [
    {
      domain,
      metric: 'd_prime',
      value: round(dPrime(hits, misses, fa, cr), 3),
      unit: 'index',
      trialsUsed: valid.length,
    },
  ]
  if (fa + cr > 0) {
    rows.push({
      domain: commissionDomain,
      metric: 'commission_rate',
      value: round(fa / (fa + cr), 4),
      unit: 'ratio',
      trialsUsed: fa + cr,
    })
  }
  if (hits + misses > 0) {
    rows.push({
      domain,
      metric: 'omission_rate',
      value: round(misses / (hits + misses), 4),
      unit: 'ratio',
      trialsUsed: hits + misses,
    })
  }
  return rows
}

export function round(v: number, digits: number): number {
  const f = 10 ** digits
  return Math.round(v * f) / f
}
